// DefaultFilterSelector.tsx — Co Marca — alineado con ReservasFilter
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    Star, Save, Trash2, X, ChevronDown,
    Filter, CheckCircle2, Settings, Plus
} from 'lucide-react';
import { filterService } from '../../services/filter/filterService';

interface Props {
    modelName: string;
    currentDefaultId?: number|null;
    currentParams: Record<string,any>;
    onDefaultChanged: () => void;
    onSaveFilter?: (name: string, params: Record<string,any>, isDefault: boolean) => Promise<void>;
    onApplyFilter?: (params: Record<string,any>|null) => void;
    onDeleteFilter?: (id: number) => void;
    onSetAsDefault?: (id: number) => Promise<void>;
    loading?: boolean;
}

const C = { primary:'#c1348b', primaryDark:'#8f1f64', secondary:'#61257b', secondaryDark:'#004d7a', secondaryLight:'#8b3fa8', accentDark:'#ea8406' };

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.dfs-wrap *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes dfsSlideDown{from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes dfsFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes spin{to{transform:rotate(360deg);}}
.dfs-panel{animation:dfsSlideDown .25s cubic-bezier(.16,1,.3,1);}
.dfs-save-dialog{animation:dfsSlideDown .3s cubic-bezier(.16,1,.3,1);}
.dfs-main-btn{
    display:inline-flex;align-items:center;gap:6px;
    padding:9px 14px;border-radius:10px;border:1.5px solid #e2e8f0;
    background:#fff;color:#374151;font-size:.83rem;font-weight:700;
    cursor:pointer;transition:all .2s;white-space:nowrap;
    font-family:'DM Sans',sans-serif;
}
.dfs-main-btn:hover{background:#f8fafc;border-color:${C.secondary}40;color:${C.secondary};}
.dfs-filter-item{
    border-radius:11px;border:1.5px solid #f1f5f9;
    padding:10px 12px;transition:all .2s;background:#fff;
}
.dfs-filter-item:hover{border-color:${C.secondary}25;background:${C.secondary}04;}
.dfs-filter-item.default{border-color:${C.accentDark}25;background:${C.accentDark}06;}
.dfs-apply-btn{
    padding:4px 10px;border-radius:7px;border:none;
    background:${C.secondaryLight}18;color:#065f46;
    font-size:.68rem;font-weight:700;cursor:pointer;transition:all .2s;
    font-family:'DM Sans',sans-serif;
}
.dfs-apply-btn:hover{background:${C.secondaryLight}30;}
.dfs-del-btn{
    width:26px;height:26px;border-radius:7px;border:none;
    background:transparent;color:#94a3b8;cursor:pointer;
    display:flex;align-items:center;justify-content:center;transition:all .2s;
}
.dfs-del-btn:hover{background:${C.primary}10;color:${C.primary};}
.dfs-input{
    width:100%;padding:10px 14px;border-radius:10px;
    border:1.5px solid #e5e7eb;background:#fafafa;
    font-size:.88rem;color:#374151;outline:none;transition:all .2s;
    font-family:'DM Sans',sans-serif;
}
.dfs-input:focus{border-color:${C.secondary};box-shadow:0 0 0 3px rgba(97, 37, 123,.1);background:#fff;}
.dfs-save-btn{
    display:inline-flex;align-items:center;gap:6px;
    padding:9px 18px;border-radius:10px;border:none;
    background:linear-gradient(135deg,${C.secondary},${C.secondaryDark});
    color:#fff;font-weight:800;font-size:.85rem;cursor:pointer;
    box-shadow:0 4px 14px rgba(97, 37, 123,.25);transition:all .22s;
    font-family:'DM Sans',sans-serif;
}
.dfs-save-btn:hover:not(:disabled){transform:translateY(-1px);}
.dfs-save-btn:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.dfs-cancel-btn{
    display:inline-flex;align-items:center;gap:6px;
    padding:9px 16px;border-radius:10px;border:1.5px solid #e2e8f0;
    background:#f8fafc;color:#374151;font-weight:700;font-size:.85rem;
    cursor:pointer;transition:all .2s;font-family:'DM Sans',sans-serif;
}
.dfs-cancel-btn:hover{background:#f1f5f9;}
.dfs-scroll::-webkit-scrollbar{width:5px;}
.dfs-scroll::-webkit-scrollbar-track{background:transparent;}
.dfs-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.25);border-radius:10px;}
.dfs-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97, 37, 123,.4);}

.dfs-panel {
    background:#fff;
    border:1.5px solid #f1f5f9;
}
.dfs-save-dialog {
    background:#fff;
    border:1.5px solid #f1f5f9;
}
.dfs-checkbox-box {
    background:#f8fafc;
    border:1.5px solid #f1f5f9;
}
.dfs-close-btn {
    width:26px;height:26px;border-radius:8px;border:1.5px solid #f1f5f9;
    background:#f8fafc;display:flex;align-items:center;justify-content:center;cursor:pointer;
    transition:all .2s;color:#64748b;
}
.dfs-close-btn:hover{background:#f1f5f9;}

/* ── Dark Mode ── */
html.dark .dfs-main-btn, [data-theme="dark"] .dfs-main-btn {
    background: #150a28 !important;
    border-color: rgba(193, 52, 139, 0.35) !important;
    color: #f1f5f9 !important;
}
html.dark .dfs-main-btn:hover, [data-theme="dark"] .dfs-main-btn:hover {
    background: rgba(193, 52, 139, 0.15) !important;
    border-color: rgba(193, 52, 139, 0.5) !important;
    color: #ffd97c !important;
}
html.dark .dfs-panel, [data-theme="dark"] .dfs-panel {
    background: #150a28 !important;
    border-color: rgba(193, 52, 139, 0.35) !important;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6) !important;
    color: #f1f5f9 !important;
}
html.dark .dfs-panel *[style*="color:#0f172a"], [data-theme="dark"] .dfs-panel *[style*="color:#0f172a"] {
    color: #f8fafc !important;
}
html.dark .dfs-filter-item, [data-theme="dark"] .dfs-filter-item {
    background: rgba(255, 255, 255, 0.04) !important;
    border-color: rgba(193, 52, 139, 0.2) !important;
    color: #f1f5f9 !important;
}
html.dark .dfs-filter-item:hover, [data-theme="dark"] .dfs-filter-item:hover {
    border-color: rgba(193, 52, 139, 0.45) !important;
    background: rgba(193, 52, 139, 0.12) !important;
}
html.dark .dfs-save-dialog, [data-theme="dark"] .dfs-save-dialog {
    background: #150a28 !important;
    border-color: rgba(193, 52, 139, 0.35) !important;
    box-shadow: 0 28px 70px rgba(0,0,0,.6) !important;
    color: #f1f5f9 !important;
}
html.dark .dfs-save-dialog *[style*="color:#0f172a"], [data-theme="dark"] .dfs-save-dialog *[style*="color:#0f172a"] {
    color: #f8fafc !important;
}
html.dark .dfs-save-dialog label, [data-theme="dark"] .dfs-save-dialog label {
    color: #cbd5e1 !important;
}
html.dark .dfs-input, [data-theme="dark"] .dfs-input {
    background: #1c0e35 !important;
    border-color: rgba(193, 52, 139, 0.3) !important;
    color: #f1f5f9 !important;
}
html.dark .dfs-checkbox-box, [data-theme="dark"] .dfs-checkbox-box {
    background: rgba(255, 255, 255, 0.04) !important;
    border-color: rgba(193, 52, 139, 0.2) !important;
}
html.dark .dfs-close-btn, [data-theme="dark"] .dfs-close-btn {
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(193, 52, 139, 0.2) !important;
    color: #cbd5e1 !important;
}
html.dark .dfs-close-btn:hover, [data-theme="dark"] .dfs-close-btn:hover {
    background: rgba(193, 52, 139, 0.2) !important;
    color: #fff !important;
}
html.dark .dfs-cancel-btn, [data-theme="dark"] .dfs-cancel-btn {
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(193, 52, 139, 0.2) !important;
    color: #cbd5e1 !important;
}
`;

const DefaultFilterSelector: React.FC<Props> = ({
    modelName, currentDefaultId, currentParams, onDefaultChanged,
    onSaveFilter, onApplyFilter, onDeleteFilter, onSetAsDefault, loading = false
}) => {
    const [showPanel, setShowPanel]         = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [availableFilters, setAvailableFilters] = useState<{personal_filters:any[];current_default_id:number|null;current_default_name:string|null}>({personal_filters:[],current_default_id:null,current_default_name:null});
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [filterName, setFilterName]       = useState('');
    const [saveAsDefault, setSaveAsDefault] = useState(false);
    const [message, setMessage]             = useState<{type:'success'|'error';text:string}|null>(null);
    const [panelPos, setPanelPos]           = useState({top:0,left:0});
    const btnRef                            = useRef<HTMLButtonElement>(null);

    const PANEL_W = 340;

    const updatePos = useCallback(()=>{
        if (!btnRef.current) return;
        const r   = btnRef.current.getBoundingClientRect();
        const vw  = window.innerWidth;
        const margin = 12; // px de margen del borde
        // Si el panel se saldría por la derecha → anclar al borde derecho del botón
        const left = r.left + PANEL_W + margin > vw
            ? Math.max(margin, r.right - PANEL_W)
            : r.left;
        setPanelPos({ top: r.bottom + 8, left });
    },[]);

    useEffect(()=>{
        if (!showPanel) return;
        updatePos();
        window.addEventListener('scroll', updatePos, true);
        window.addEventListener('resize', updatePos);
        return()=>{ window.removeEventListener('scroll',updatePos,true); window.removeEventListener('resize',updatePos); };
    },[showPanel,updatePos]);

    const loadFilters = useCallback(async()=>{
        setLoadingFilters(true); setMessage(null);
        try { setAvailableFilters(await filterService.getAvailableDefaults(modelName)); }
        catch(e:any){ setMessage({type:'error',text:e.message||'Error cargando filtros'}); }
        finally { setLoadingFilters(false); }
    },[modelName]);

    useEffect(()=>{ if(showPanel) loadFilters(); },[showPanel,loadFilters]);

    const handleSetDefault = async(f:any)=>{
        if (!onSetAsDefault) return;
        try { await onSetAsDefault(f.id); setMessage({type:'success',text:`"${f.filter_name}" establecido como predeterminado`}); onDefaultChanged(); await loadFilters(); }
        catch(e:any){ setMessage({type:'error',text:e.message||'Error'}); }
    };
    const handleDelete = async(id:number)=>{
        if (!onDeleteFilter||!window.confirm('¿Eliminar este filtro?')) return;
        try { await onDeleteFilter(id); setMessage({type:'success',text:'Filtro eliminado'}); await loadFilters(); onDefaultChanged(); }
        catch(e:any){ setMessage({type:'error',text:e.message||'Error'}); }
    };
    const handleApply = (f:any)=>{ if(onApplyFilter){ onApplyFilter(f.parameters); setShowPanel(false); } };
    const handleSave  = async()=>{
        if (!filterName.trim()||!onSaveFilter) return;
        try { await onSaveFilter(filterName.trim(),currentParams,saveAsDefault); setFilterName(''); setSaveAsDefault(false); setShowSaveDialog(false); setMessage({type:'success',text:'Filtro guardado'}); await loadFilters(); onDefaultChanged(); }
        catch(e:any){ setMessage({type:'error',text:`Error: ${e.message||'desconocido'}`}); }
    };

    const sorted=[...availableFilters.personal_filters].sort((a,b)=>{
        if(a.id===availableFilters.current_default_id) return -1;
        if(b.id===availableFilters.current_default_id) return 1;
        return a.filter_name.localeCompare(b.filter_name);
    });

    const hasParams = Object.keys(currentParams).length>0;

    return (
        <>
            <style>{css}</style>
            <div className="dfs-wrap" style={{position:'relative'}}>
                <button ref={btnRef} className="dfs-main-btn" onClick={()=>{ if(!showPanel) updatePos(); setShowPanel(p=>!p); }}>
                    <Settings size={14} color={C.secondary}/>
                    <span>Filtros guardados</span>
                    <ChevronDown size={12} color="#94a3b8" style={{transform:showPanel?'rotate(180deg)':'rotate(0)',transition:'transform .2s'}}/>
                </button>

                {/* Panel principal — portal */}
                {showPanel&&createPortal(
                    <>
                        <div style={{position:'fixed',inset:0,zIndex:99990}} onClick={()=>setShowPanel(false)}/>
                        <div className="dfs-panel dfs-scroll" style={{position:'fixed',top:panelPos.top,left:panelPos.left,width:'340px',maxWidth:'calc(100vw - 2rem)',borderRadius:'18px',boxShadow:'0 20px 60px rgba(0,0,0,.16)',zIndex:99991,overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'80vh'}}>
                            {/* Barra colores */}
                            <div style={{height:'4px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})`,flexShrink:0}}/>

                            {/* Header */}
                            <div style={{padding:'14px 16px',borderBottom:'1.5px solid rgba(193,52,139,0.15)',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                                    <div style={{width:'30px',height:'30px',borderRadius:'9px',background:`${C.secondary}10`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                        <Filter size={14} color={C.secondary}/>
                                    </div>
                                    <div>
                                        <p style={{fontSize:'.88rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.01em'}}>Filtros Guardados</p>
                                        <p style={{fontSize:'.65rem',color:'#94a3b8',marginTop:'1px'}}>Gestiona tus filtros personalizados</p>
                                    </div>
                                </div>
                                <button onClick={()=>setShowPanel(false)} className="dfs-close-btn">
                                    <X size={12}/>
                                </button>
                            </div>

                            {/* Predeterminado actual */}
                            {availableFilters.current_default_name&&(
                                <div style={{margin:'12px 14px 0',padding:'9px 12px',borderRadius:'10px',background:`${C.accentDark}08`,border:`1.5px solid ${C.accentDark}20`,display:'flex',alignItems:'center',gap:'7px',flexShrink:0}}>
                                    <Star size={12} color={C.accentDark} fill={C.accentDark}/>
                                    <div>
                                        <p style={{fontSize:'.65rem',color:'#94a3b8',fontWeight:'600',textTransform:'uppercase',letterSpacing:'.04em'}}>Predeterminado</p>
                                        <p style={{fontSize:'.8rem',fontWeight:'700',color:'#374151'}}>{availableFilters.current_default_name}</p>
                                    </div>
                                </div>
                            )}

                            {/* Body scrollable */}
                            <div className="dfs-scroll" style={{flex:1,overflowY:'auto',padding:'12px 14px',display:'flex',flexDirection:'column',gap:'8px'}}>
                                {/* Mensaje */}
                                {message&&(
                                    <div style={{padding:'8px 12px',borderRadius:'8px',fontSize:'.78rem',fontWeight:'600',display:'flex',alignItems:'center',gap:'6px',background:message.type==='success'?`${C.secondaryLight}10`:`${C.primary}08`,color:message.type==='success'?'#065f46':C.primaryDark,border:`1px solid ${message.type==='success'?C.secondaryLight+'30':C.primary+'22'}`}}>
                                        {message.type==='success'?<CheckCircle2 size={12}/>:<X size={12}/>}
                                        {message.text}
                                    </div>
                                )}

                                {/* Guardar filtro actual */}
                                {hasParams&&onSaveFilter&&(
                                    <button onClick={()=>setShowSaveDialog(true)} style={{width:'100%',padding:'9px',borderRadius:'10px',border:`1.5px dashed ${C.secondary}30`,background:`${C.secondary}06`,color:C.secondary,fontSize:'.8rem',fontWeight:'700',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:'5px',transition:'all .2s'}}
                                        onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background=`${C.secondary}10`}
                                        onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background=`${C.secondary}06`}>
                                        <Plus size={13}/> Guardar filtro actual
                                    </button>
                                )}

                                {/* Lista */}
                                {loadingFilters?(
                                    <div style={{textAlign:'center',padding:'24px',display:'flex',flexDirection:'column',alignItems:'center',gap:'8px'}}>
                                        <div style={{width:'28px',height:'28px',border:`3px solid ${C.secondary}`,borderTopColor:'transparent',borderRadius:'50%',animation:'spin .7s linear infinite'}}/>
                                        <p style={{fontSize:'.78rem',color:'#94a3b8',fontWeight:'500'}}>Cargando filtros...</p>
                                    </div>
                                ):sorted.length===0?(
                                    <div style={{textAlign:'center',padding:'24px',border:'1.5px dashed #e2e8f0',borderRadius:'12px'}}>
                                        <Filter size={24} color="#d1d5db" style={{margin:'0 auto 8px'}}/>
                                        <p style={{fontSize:'.8rem',color:'#94a3b8',fontWeight:'500'}}>Sin filtros guardados</p>
                                    </div>
                                ):sorted.map(f=>(
                                    <div key={f.id} className={`dfs-filter-item${f.id===availableFilters.current_default_id?' default':''}`}>
                                        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                                            <button onClick={()=>f.id!==availableFilters.current_default_id&&handleSetDefault(f)} title={f.id===availableFilters.current_default_id?'Predeterminado':'Establecer como predeterminado'}
                                                style={{background:'none',border:'none',cursor:'pointer',padding:'2px',display:'flex',alignItems:'center',flexShrink:0}}>
                                                <Star size={14} color={f.id===availableFilters.current_default_id?C.accentDark:'#d1d5db'} fill={f.id===availableFilters.current_default_id?C.accentDark:'none'}/>
                                            </button>
                                            <div style={{flex:1,minWidth:0}}>
                                                <p style={{fontSize:'.82rem',fontWeight:'700',color:'#0f172a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.filter_name}</p>
                                                <p style={{fontSize:'.65rem',color:'#94a3b8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{Object.keys(f.parameters||{}).join(', ')}</p>
                                            </div>
                                            <div style={{display:'flex',gap:'4px',flexShrink:0}}>
                                                {onApplyFilter&&<button className="dfs-apply-btn" onClick={()=>handleApply(f)}>Aplicar</button>}
                                                {onDeleteFilter&&<button className="dfs-del-btn" onClick={()=>handleDelete(f.id)}><Trash2 size={13}/></button>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>,
                    document.body
                )}

                {/* Diálogo guardar */}
                {showSaveDialog&&createPortal(
                    <>
                        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,.4)',backdropFilter:'blur(4px)',zIndex:99992,animation:'dfsFadeIn .2s ease'}} onClick={()=>{setShowSaveDialog(false);setFilterName('');setSaveAsDefault(false);}}/>
                        <div style={{position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px',zIndex:99993,pointerEvents:'none'}}>
                            <div className="dfs-save-dialog" onClick={e=>e.stopPropagation()} style={{borderRadius:'20px',width:'100%',maxWidth:'400px',boxShadow:'0 28px 70px rgba(0,0,0,.18)',overflow:'hidden',pointerEvents:'all'}}>
                                <div style={{height:'4px',background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})`}}/>
                                <div style={{padding:'18px 20px',borderBottom:'1.5px solid rgba(193,52,139,0.15)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                    <div style={{display:'flex',alignItems:'center',gap:'9px'}}>
                                        <div style={{width:'32px',height:'32px',borderRadius:'9px',background:`${C.secondary}10`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                            <Save size={14} color={C.secondary}/>
                                        </div>
                                        <div>
                                            <p style={{fontSize:'.92rem',fontWeight:'900',color:'#0f172a',letterSpacing:'-.02em'}}>Guardar Filtro</p>
                                            <p style={{fontSize:'.68rem',color:'#94a3b8',marginTop:'1px'}}>Guarda la configuración actual</p>
                                        </div>
                                    </div>
                                    <button onClick={()=>{setShowSaveDialog(false);setFilterName('');setSaveAsDefault(false);}} className="dfs-close-btn">
                                        <X size={13}/>
                                    </button>
                                </div>
                                <div style={{padding:'16px 20px 20px',display:'flex',flexDirection:'column',gap:'12px'}}>
                                    <div>
                                        <label style={{display:'block',fontSize:'.68rem',fontWeight:'800',color:'#374151',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:'7px'}}>Nombre del filtro *</label>
                                        <input type="text" autoFocus value={filterName} onChange={e=>setFilterName(e.target.value)} placeholder="Ej: Confirmadas Sucursal Central" className="dfs-input"/>
                                    </div>
                                    <div className="dfs-checkbox-box" style={{borderRadius:'11px',padding:'11px 14px',display:'flex',alignItems:'flex-start',gap:'10px'}}>
                                        <input type="checkbox" id="dfs-default" checked={saveAsDefault} onChange={e=>setSaveAsDefault(e.target.checked)} style={{marginTop:'2px',width:'14px',height:'14px',accentColor:C.secondary,cursor:'pointer'}}/>
                                        <label htmlFor="dfs-default" style={{cursor:'pointer'}}>
                                            <p style={{fontSize:'.82rem',fontWeight:'700',color:'#0f172a'}}>Establecer como predeterminado</p>
                                            <p style={{fontSize:'.68rem',color:'#94a3b8',marginTop:'2px'}}>Se aplicará automáticamente al cargar la página</p>
                                        </label>
                                    </div>
                                    <div style={{display:'flex',justifyContent:'flex-end',gap:'8px',paddingTop:'4px',borderTop:'1.5px solid #f1f5f9',marginTop:'2px'}}>
                                        <button onClick={()=>{setShowSaveDialog(false);setFilterName('');setSaveAsDefault(false);}} className="dfs-cancel-btn">Cancelar</button>
                                        <button onClick={handleSave} disabled={!filterName.trim()||loading} className="dfs-save-btn">
                                            {loading?<><span style={{width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block'}}/> Guardando...</>:<><Save size={13}/> Guardar</>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>,
                    document.body
                )}
            </div>
        </>
    );
};

export default DefaultFilterSelector;